// Text catalog for the home page. Images, asset imports and link targets stay
// in src/pages/HomePage.tsx; this holds only translatable strings, aligned by
// array index with the meta arrays in the component.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type HomeStrings = {
  meta: PageMeta;
  hero: { srTitle: string; tagline: string; body: string; cta: string; videoTitle: string };
  kollega: {
    heading: string;
    body1: string;
    body2: string;
    workplaceBtn: string;
    positionsBtn: string;
  };
  servicesSection: { heading: string; intro: string; readMore: string };
  /** Aligned by index with serviceCardMeta in HomePage. */
  serviceCards: { title: string; text: string }[];
  faser: { heading: string; paras: string[] };
  partnersSection: { heading: string; body: string; cta: string };
  garanti: { heading: string; para1: string; para2: string };
  samarbejde: { heading: string; para1: string; para2: string };
  sikkerhed: { heading: string; para1: string; para2: string };
  ledelse: { heading: string; roles: string[] };
  miit: { body: string; readMorePrefix: string };
  digitale: { heading: string; para1: string; para2: string };
  cta: { heading: string; body: string; call: string; write: string };
  blog: { heading: string; intro: string; readMore: string };
};

export const homeStrings: Record<Locale, HomeStrings> = {
  da: {
    meta: {
      title: "Bygge-, projekt- og sikkerhedsledelse - MI Support",
      description:
        "MI Support er en dynamisk konsulentvirksomhed, der tilbyder bygge-, sikkerheds- og projektledere til at styre større byggeprocesser i anlægs- og procesfasen.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg",
    },
    hero: {
      srTitle: "MI Support - Bygge-, projekt- og sikkerhedsledelse",
      videoTitle: "MI Support - konsulenter der driver jeres byggeprojekt",
      tagline: "Få hjælp til jeres byggeprojekt, fra designfase til handover",
      body: "MI Support er en dynamisk konsulentvirksomhed, der tilbyder bygge-, sikkerheds- og projektledere til at styre større byggeprocesser, både i anlægs- og procesfasen. Vores konsulenter udgør også plannere, controllere og projektassistenter, der kan assistere i hele eller dele af byggeperioden.",
      cta: "Kontakt os i dag",
    },
    kollega: {
      heading: "Er du vores nye kollega?",
      body1:
        "I MI Support er vi altid på udkig efter nye, dygtige kollegaer, der har lyst til at blive en del af vores team. Vi har jævnligt ledige stillinger slået op, og ellers er du altid velkommen til at skrive dig op i vores CV-bank, som vi løbende screener for interessante profiler.",
      body2:
        "MI Support er en dynamisk arbejdsplads, hvor der ikke er langt fra tanke til handling. Vi tilbyder engagerede og gode kollegaer med fokus på udvikling og faglig sparring. Herudover har vi gode og stabile arbejdsvilkår, og vi lægger vægt på et rart arbejdsmiljø med hyppige sociale arrangementer.",
      workplaceBtn: "som arbejdsplads",
      positionsBtn: "Se ledige stillinger",
    },
    servicesSection: {
      heading: "Vores ydelser",
      intro:
        "Vi tilbyder et bredt udvalg af konsulentydelser inden for bygge-, projekt- og sikkerhedsledelse. Vores erfarne konsulenter er klar til at hjælpe jer gennem hele byggeprocessen.",
      readMore: "Læs mere",
    },
    serviceCards: [
      {
        title: "Projektledelse",
        text: "Erfarne projektledere skaber fremdrift, koordinerer aktiviteter og håndterer risici, så I styrer projektet sikkert i mål med færre forsinkelser og fuldt overblik.",
      },
      {
        title: "Byggeledelse",
        text: "Vores byggeledere tager projektet fra idé til virkelighed og planlægger og styrer jeres næste bygge- eller anlægsprojekt med stærke kompetencer og bred erfaring.",
      },
      {
        title: "Bygherrerådgivning",
        text: "Som uafhængig bygherrerådgiver varetager vi bygherrens interesser fra strategi og udbud til ibrugtagning, med styr på økonomi, tid, kvalitet, risici og bæredygtighed.",
      },
      {
        title: "Sikkerhedsledelse (HSE)",
        text: "Sikkerhedskoordinatorer der forebygger ulykker og minimerer risici for skader og sygdom, så alle kommer trygt hjem fra pladsen hver dag.",
      },
      {
        title: "Tidsplanlægning",
        text: "Robuste hovedtidsplaner og fremdriftsstyring i Primavera P6 og MS Project, så komplekse byggerier afleveres til tiden.",
      },
      {
        title: "Projektøkonomi",
        text: "Projektcontrollere tager hånd om økonomien fra estimat og budget til forecast, cost control og slutregnskab, med fuld transparens for bygherre og ledelse.",
      },
      {
        title: "Projektassistance",
        text: "Projektassistenter styrker organisationen med dokumentstyring, mødekoordinering og rapportering, så projektledelsen kan fokusere på fremdrift og beslutninger.",
      },
      {
        title: "Planlægning og ledelse af shutdowns",
        text: "Erfarne shutdown managers leder turnarounds og revisioner på industri- og produktionsanlæg, med minimal nedetid og fuld kontrol over scope, tid og sikkerhed.",
      },
      {
        title: "SiteSupport",
        text: "Vores digitale platform til byggepladsen, der samler dokumentation, registreringer og kommunikation ét sted for projektledelse, byggeledelse og HSE.",
      },
    ],
    faser: {
      heading: "Få hjælp til alle faser af byggeriet af en erfaren rådgiver",
      paras: [
        "I MI Support har vi mange års erfaring, og vores bygge- og projektledere kan håndtere alle led i byggeprocessen, fra idé og design til ibrugtagning og drift.",
        "I MI Support er sikkerhed en naturlig del af alle processer og arbejdsopgaver. På alle byggeprojekter, uanset projektets størrelse og kompleksitet, arbejder vi målrettet med at skabe trygge rammer, hvor trivsel, ansvarlighed og effektiv drift går hånd i hånd.",
        "Vores konsulenter bidrager med struktur, erfaring og fokus på forebyggelse, så risici minimeres, og arbejdspladsen forbliver sikker for alle involverede. Det skaber større stabilitet i projekterne og bedre forudsætninger for et stærkt samarbejde.",
      ],
    },
    partnersSection: {
      heading: "Udvalgte samarbejdspartnere",
      body: "Vi har mange forskellige samarbejdspartnere, fra ambitiøse virksomheder til nogle af landets største bygherrer, og vi bidrager med den faglige tyngde, der skal til for at føre deres komplekse byggeprojekter sikkert i mål.",
      cta: "Se alle vores samarbejdspartnere",
    },
    garanti: {
      heading: "Jeres garanti for et godt resultat",
      para1:
        "MI Support er baseret på handlekraftige, ansvarlige og motiverede medarbejdere. Medarbejdere, der har særlige kompetencer og viden inden for bygge-, projekt- og sikkerhedsledelse, der med faglig stolthed arbejder målrettet og engageret med opgaver.",
      para2:
        "Vores erfaring giver os den viden, det kræver at levere et godt resultat hver gang. Vi arbejder hurtigt og målrettet, og vi yder vores bedste i døgnets 24 timer. Vi overholder alle krav om kvalitet, tid og økonomi med stort sideløbende fokus på sundhed, miljø og sikkerhed.",
    },
    samarbejde: {
      heading: "Samarbejde og åbenhed skaber det bedste fundament",
      para1:
        "Vores konsulenter lægger stor vægt på klar kommunikation, kontrol og tæt samarbejde gennem hele projektforløbet. Det sikrer, at alle interessenter løbende har indsigt i projektets udvikling, og at forventninger og resultater er afstemt hele vejen.",
      para2:
        "I MI Support tager vi altid udgangspunkt i den enkelte opgave og udvikler løsninger i tæt samarbejde med vores kunder og samarbejdspartnere. På den måde skaber vi de bedste forudsætninger for effektive processer og stærke resultater.",
    },
    sikkerhed: {
      heading: "En kompromisløs tilgang til sikkerhed og arbejdsmiljø",
      para1:
        "På alle byggeprojekter, uanset projektets størrelse og kompleksitet, arbejder vi målrettet med at skabe trygge rammer, hvor trivsel, ansvarlighed og effektiv drift går hånd i hånd.",
      para2:
        "Vores konsulenter bidrager med struktur, erfaring og fokus på forebyggelse, så risici minimeres, og arbejdspladsen forbliver sikker for alle involverede. Det skaber større stabilitet i projekterne og bedre forudsætninger for et stærkt samarbejde.",
    },
    ledelse: {
      heading: "Ledelsen i MI Support",
      roles: ["Stifter & ejer", "IT & økonomi", "Byggeledelse & HR", "Byggeledelse & kvalitet"],
    },
    miit: {
      body: "MI Support er en dynamisk og flergrenet konsulentvirksomhed, der også tilbyder support inden for IT-området i form af projektledelse, rådgivning og sikkerhedsløsninger til mange forskellige brancher, bl.a. også byggebranchen.",
      readMorePrefix: "Læs mere om",
    },
    digitale: {
      heading: "Digitale værktøjer for øget sikkerhed, tryghed og større fleksibilitet",
      para1:
        "I MI Support benytter vi os af den nyeste teknologi og digitale værktøjer til at registrere byggepladsens fremdrift og udvikling. Det er værdifulde redskaber, der bruges i den daglige koordinering og planlægning af byggeriet.",
      para2:
        "Det skaber et bedre og mere konstruktivt grundlag på bygge- og koordineringsmøderne, og det gør det muligt at få indblik i byggeriets nuværende status. Med dette udgangspunkt minimeres fejl, da vi kan sidde med realtidsoverblik ved mødebordet og tage vigtige beslutninger med det samme.",
    },
    cta: {
      heading: "Skal vi assistere på jeres næste byggeprojekt?",
      body: "Uanset om I står foran et mindre byggeprojekt eller et større, komplekst byggeri, står vi klar til at hjælpe jer sikkert i mål. Tag fat i os for en uforpligtende snak om, hvordan MI Support kan løfte jeres projekt.",
      call: "Ring +45 5555 1212",
      write: "Skriv til os",
    },
    blog: {
      heading: "Læs vores seneste blogindlæg",
      intro:
        "Bliv klogere på MI Support gennem vores artikler her på siden. Vi deler løbende alt fra nyheder i virksomheden til tips og tricks såvel som interessante perspektiver på byggebranchen.",
      readMore: "Læs mere",
    },
  },

  en: {
    meta: {
      title: "MI Support - Construction, project and safety management",
      description:
        "MI Support is a dynamic consultancy providing construction, safety and project managers to lead major construction projects from design to handover.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg",
    },
    hero: {
      srTitle: "MI Support - Construction, project and safety management",
      videoTitle: "MI Support - consultants who drive your construction project",
      tagline: "Get help with your construction project, from design phase to handover",
      body: "MI Support is a dynamic consultancy providing construction, safety and project managers to lead major construction processes, both in the construction and process phases. Our consultants also include planners, controllers and project assistants who can assist during all or part of the construction period.",
      cta: "Contact us today",
    },
    kollega: {
      heading: "Are you our new colleague?",
      body1:
        "At MI Support we are always looking for new, talented colleagues who want to become part of our team. We regularly post open positions, and otherwise you are always welcome to add yourself to our CV bank, which we continuously screen for interesting profiles.",
      body2:
        "MI Support is a dynamic workplace where it's a short step from thought to action. We offer engaged, great colleagues with a focus on development and professional sparring. On top of that, we have good and stable working conditions, and we value a pleasant working environment with frequent social events.",
      workplaceBtn: "as a workplace",
      positionsBtn: "See open positions",
    },
    servicesSection: {
      heading: "Our services",
      intro:
        "We offer a broad range of consultancy services within construction, project and safety management. Our experienced consultants are ready to help you through the entire construction process.",
      readMore: "Read more",
    },
    serviceCards: [
      {
        title: "Project management",
        text: "Experienced project managers create momentum, coordinate activities and handle risks, so you steer the project safely to completion with fewer delays and full overview.",
      },
      {
        title: "Construction management",
        text: "Our construction managers take the project from idea to reality, planning and steering your next building or civil project with strong skills and broad experience.",
      },
      {
        title: "Client consultancy",
        text: "As an independent client consultant we safeguard the client's interests from strategy and tender to commissioning, with control over economy, time, quality, risk and sustainability.",
      },
      {
        title: "Safety management (HSE)",
        text: "Safety coordinators who prevent accidents and minimise the risk of injury and illness, so everyone gets home safely from the site every day.",
      },
      {
        title: "Scheduling",
        text: "Robust master schedules and progress control in Primavera P6 and MS Project, so complex builds are delivered on time.",
      },
      {
        title: "Project economics",
        text: "Project controllers handle the economy from estimate and budget to forecast, cost control and final accounts, with full transparency for client and management.",
      },
      {
        title: "Project assistance",
        text: "Project assistants strengthen the organisation with document control, meeting coordination and reporting, so the project management can focus on progress and decisions.",
      },
      {
        title: "Shutdown planning and management",
        text: "Experienced shutdown managers lead turnarounds and overhauls at industrial and production plants, with minimal downtime and full control over scope, time and safety.",
      },
      {
        title: "SiteSupport",
        text: "Our digital platform for the construction site, gathering documentation, registrations and communication in one place for project management, construction management and HSE.",
      },
    ],
    faser: {
      heading: "Get help with every phase of construction from an experienced advisor",
      paras: [
        "At MI Support we have many years of experience, and our construction and project managers can handle every stage of the construction process, from idea and design to commissioning and operation.",
        "At MI Support, safety is a natural part of all processes and tasks. On every construction project, regardless of its size and complexity, we work purposefully to create safe frameworks where well-being, responsibility and efficient operation go hand in hand.",
        "Our consultants contribute structure, experience and a focus on prevention, so risks are minimised and the workplace stays safe for everyone involved. This creates greater stability in the projects and better conditions for strong collaboration.",
      ],
    },
    partnersSection: {
      heading: "Selected partners",
      body: "We have many different partners, from ambitious companies to some of the country's largest clients, and we contribute the professional weight needed to bring their complex construction projects safely to completion.",
      cta: "See all our partners",
    },
    garanti: {
      heading: "Your guarantee of a good result",
      para1:
        "MI Support is built on proactive, responsible and motivated employees. Employees with special skills and knowledge within construction, project and safety management, who work purposefully and with professional pride.",
      para2:
        "Our experience gives us the knowledge it takes to deliver a good result every time. We work quickly and purposefully, and we give our best around the clock. We meet all requirements for quality, time and economy with a strong parallel focus on health, environment and safety.",
    },
    samarbejde: {
      heading: "Collaboration and openness create the best foundation",
      para1:
        "Our consultants place great emphasis on clear communication, control and close collaboration throughout the project. This ensures that all stakeholders continuously have insight into the project's development, and that expectations and results are aligned all the way.",
      para2:
        "At MI Support we always take the individual task as our starting point and develop solutions in close collaboration with our clients and partners. That way we create the best conditions for efficient processes and strong results.",
    },
    sikkerhed: {
      heading: "An uncompromising approach to safety and the working environment",
      para1:
        "On every construction project, regardless of its size and complexity, we work purposefully to create safe frameworks where well-being, responsibility and efficient operation go hand in hand.",
      para2:
        "Our consultants contribute structure, experience and a focus on prevention, so risks are minimised and the workplace stays safe for everyone involved. This creates greater stability in the projects and better conditions for strong collaboration.",
    },
    ledelse: {
      heading: "The management at MI Support",
      roles: ["Founder & owner", "IT & finance", "Construction management & HR", "Construction management & quality"],
    },
    miit: {
      body: "MI Support is a dynamic, multi-branch consultancy that also offers support within IT in the form of project management, advisory and security solutions for many different industries, including the construction industry.",
      readMorePrefix: "Read more about",
    },
    digitale: {
      heading: "Digital tools for greater safety, security and flexibility",
      para1:
        "At MI Support we use the latest technology and digital tools to record the construction site's progress and development. These are valuable tools used in the daily coordination and planning of the construction.",
      para2:
        "It creates a better and more constructive basis at the construction and coordination meetings, and it makes it possible to gain insight into the construction's current status. On this basis, errors are minimised, as we can sit with a real-time overview at the meeting table and make important decisions right away.",
    },
    cta: {
      heading: "Shall we assist on your next construction project?",
      body: "Whether you're facing a smaller construction project or a larger, complex build, we're ready to help you safely to the finish. Get in touch for a no-obligation chat about how MI Support can lift your project.",
      call: "Call +45 5555 1212",
      write: "Write to us",
    },
    blog: {
      heading: "Read our latest blog posts",
      intro:
        "Get to know MI Support through our articles here on the site. We continuously share everything from company news to tips and tricks as well as interesting perspectives on the construction industry.",
      readMore: "Read more",
    },
  },

  de: {
    meta: {
      title: "MI Support - Bau-, Projekt- und Sicherheitsmanagement",
      description:
        "MI Support ist ein dynamisches Beratungsunternehmen mit Bau-, Sicherheits- und Projektleitern für große Bauprojekte - von der Planung bis zur Übergabe.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg",
    },
    hero: {
      srTitle: "MI Support - Bau-, Projekt- und Sicherheitsmanagement",
      videoTitle: "MI Support - Berater, die Ihr Bauprojekt voranbringen",
      tagline: "Erhalten Sie Unterstützung für Ihr Bauprojekt – von der Designphase bis zur Übergabe",
      body: "MI Support ist ein dynamisches Beratungsunternehmen, das Bau-, Sicherheits- und Projektleiter bereitstellt, um große Bauprozesse zu steuern – sowohl in der Bau- als auch in der Prozessphase. Zu unseren Beratern gehören auch Planer, Controller und Projektassistenten, die während des gesamten oder eines Teils des Bauzeitraums unterstützen können.",
      cta: "Kontaktieren Sie uns noch heute",
    },
    kollega: {
      heading: "Bist du unser neuer Kollege?",
      body1:
        "Bei MI Support suchen wir immer nach neuen, talentierten Kollegen, die Teil unseres Teams werden möchten. Wir schreiben regelmäßig offene Stellen aus, und ansonsten kannst du dich jederzeit in unsere CV-Datenbank eintragen, die wir laufend nach interessanten Profilen durchsehen.",
      body2:
        "MI Support ist ein dynamischer Arbeitsplatz, an dem der Weg von der Idee zur Umsetzung kurz ist. Wir bieten engagierte, gute Kollegen mit Fokus auf Entwicklung und fachlichen Austausch. Darüber hinaus haben wir gute und stabile Arbeitsbedingungen und legen Wert auf ein angenehmes Arbeitsumfeld mit regelmäßigen sozialen Veranstaltungen.",
      workplaceBtn: "als Arbeitgeber",
      positionsBtn: "Offene Stellen ansehen",
    },
    servicesSection: {
      heading: "Unsere Leistungen",
      intro:
        "Wir bieten ein breites Spektrum an Beratungsleistungen im Bereich Bau-, Projekt- und Sicherheitsmanagement. Unsere erfahrenen Berater sind bereit, Ihnen durch den gesamten Bauprozess zu helfen.",
      readMore: "Mehr erfahren",
    },
    serviceCards: [
      {
        title: "Projektleitung",
        text: "Erfahrene Projektleiter schaffen Fortschritt, koordinieren Aktivitäten und steuern Risiken, sodass Sie das Projekt sicher mit weniger Verzögerungen und vollem Überblick ans Ziel bringen.",
      },
      {
        title: "Baumanagement",
        text: "Unsere Bauleiter bringen das Projekt von der Idee zur Realität und planen und steuern Ihr nächstes Bau- oder Anlagenprojekt mit starken Kompetenzen und breiter Erfahrung.",
      },
      {
        title: "Bauherrenberatung",
        text: "Als unabhängiger Bauherrenberater wahren wir die Interessen des Bauherrn von Strategie und Ausschreibung bis zur Inbetriebnahme – mit Kontrolle über Wirtschaftlichkeit, Zeit, Qualität, Risiken und Nachhaltigkeit.",
      },
      {
        title: "Sicherheitsmanagement (HSE)",
        text: "Sicherheitskoordinatoren, die Unfälle verhindern und das Risiko von Verletzungen und Krankheit minimieren, sodass alle jeden Tag sicher von der Baustelle nach Hause kommen.",
      },
      {
        title: "Terminplanung",
        text: "Robuste Hauptterminpläne und Fortschrittssteuerung in Primavera P6 und MS Project, sodass komplexe Bauvorhaben termingerecht übergeben werden.",
      },
      {
        title: "Projektökonomie",
        text: "Projektcontroller kümmern sich um die Wirtschaftlichkeit von Schätzung und Budget bis zu Forecast, Cost Control und Schlussabrechnung – mit voller Transparenz für Bauherr und Leitung.",
      },
      {
        title: "Projektassistenz",
        text: "Projektassistenten stärken die Organisation mit Dokumentensteuerung, Besprechungskoordination und Berichtswesen, sodass sich die Projektleitung auf Fortschritt und Entscheidungen konzentrieren kann.",
      },
      {
        title: "Shutdown-Planung und -Leitung",
        text: "Erfahrene Shutdown-Manager leiten Turnarounds und Revisionen an Industrie- und Produktionsanlagen – mit minimaler Ausfallzeit und voller Kontrolle über Scope, Zeit und Sicherheit.",
      },
      {
        title: "SiteSupport",
        text: "Unsere digitale Plattform für die Baustelle, die Dokumentation, Registrierungen und Kommunikation an einem Ort für Projektleitung, Baumanagement und HSE zusammenführt.",
      },
    ],
    faser: {
      heading: "Erhalten Sie Unterstützung in allen Bauphasen von einem erfahrenen Berater",
      paras: [
        "Bei MI Support haben wir langjährige Erfahrung, und unsere Bau- und Projektleiter können alle Schritte des Bauprozesses bewältigen – von Idee und Design bis zu Inbetriebnahme und Betrieb.",
        "Bei MI Support ist Sicherheit ein natürlicher Bestandteil aller Prozesse und Aufgaben. Bei allen Bauprojekten, unabhängig von Größe und Komplexität, arbeiten wir zielgerichtet daran, sichere Rahmenbedingungen zu schaffen, in denen Wohlbefinden, Verantwortung und effizienter Betrieb Hand in Hand gehen.",
        "Unsere Berater bringen Struktur, Erfahrung und einen Fokus auf Prävention ein, sodass Risiken minimiert werden und der Arbeitsplatz für alle Beteiligten sicher bleibt. Das schafft mehr Stabilität in den Projekten und bessere Voraussetzungen für eine starke Zusammenarbeit.",
      ],
    },
    partnersSection: {
      heading: "Ausgewählte Partner",
      body: "Wir haben viele verschiedene Partner, von ambitionierten Unternehmen bis zu einigen der größten Bauherren des Landes, und wir bringen das fachliche Gewicht ein, das nötig ist, um ihre komplexen Bauprojekte sicher ans Ziel zu bringen.",
      cta: "Alle unsere Partner ansehen",
    },
    garanti: {
      heading: "Ihre Garantie für ein gutes Ergebnis",
      para1:
        "MI Support basiert auf tatkräftigen, verantwortungsbewussten und motivierten Mitarbeitern. Mitarbeiter mit besonderen Kompetenzen und Wissen im Bereich Bau-, Projekt- und Sicherheitsmanagement, die mit fachlichem Stolz zielgerichtet und engagiert arbeiten.",
      para2:
        "Unsere Erfahrung gibt uns das Wissen, das nötig ist, um jedes Mal ein gutes Ergebnis zu liefern. Wir arbeiten schnell und zielgerichtet und geben rund um die Uhr unser Bestes. Wir erfüllen alle Anforderungen an Qualität, Zeit und Wirtschaftlichkeit mit starkem parallelem Fokus auf Gesundheit, Umwelt und Sicherheit.",
    },
    samarbejde: {
      heading: "Zusammenarbeit und Offenheit schaffen das beste Fundament",
      para1:
        "Unsere Berater legen großen Wert auf klare Kommunikation, Kontrolle und enge Zusammenarbeit während des gesamten Projektverlaufs. Das stellt sicher, dass alle Beteiligten laufend Einblick in die Entwicklung des Projekts haben und dass Erwartungen und Ergebnisse durchgehend abgestimmt sind.",
      para2:
        "Bei MI Support gehen wir immer von der einzelnen Aufgabe aus und entwickeln Lösungen in enger Zusammenarbeit mit unseren Kunden und Partnern. So schaffen wir die besten Voraussetzungen für effiziente Prozesse und starke Ergebnisse.",
    },
    sikkerhed: {
      heading: "Ein kompromissloser Ansatz für Sicherheit und Arbeitsumfeld",
      para1:
        "Bei allen Bauprojekten, unabhängig von Größe und Komplexität, arbeiten wir zielgerichtet daran, sichere Rahmenbedingungen zu schaffen, in denen Wohlbefinden, Verantwortung und effizienter Betrieb Hand in Hand gehen.",
      para2:
        "Unsere Berater bringen Struktur, Erfahrung und einen Fokus auf Prävention ein, sodass Risiken minimiert werden und der Arbeitsplatz für alle Beteiligten sicher bleibt. Das schafft mehr Stabilität in den Projekten und bessere Voraussetzungen für eine starke Zusammenarbeit.",
    },
    ledelse: {
      heading: "Die Geschäftsleitung von MI Support",
      roles: ["Gründer & Inhaber", "IT & Finanzen", "Baumanagement & HR", "Baumanagement & Qualität"],
    },
    miit: {
      body: "MI Support ist ein dynamisches, vielseitiges Beratungsunternehmen, das auch Unterstützung im IT-Bereich in Form von Projektleitung, Beratung und Sicherheitslösungen für viele verschiedene Branchen anbietet – unter anderem auch für die Baubranche.",
      readMorePrefix: "Mehr erfahren über",
    },
    digitale: {
      heading: "Digitale Werkzeuge für mehr Sicherheit, Vertrauen und größere Flexibilität",
      para1:
        "Bei MI Support nutzen wir die neueste Technologie und digitale Werkzeuge, um den Fortschritt und die Entwicklung der Baustelle zu erfassen. Das sind wertvolle Hilfsmittel, die in der täglichen Koordination und Planung des Baus eingesetzt werden.",
      para2:
        "Das schafft eine bessere und konstruktivere Grundlage bei den Bau- und Koordinationsbesprechungen und ermöglicht Einblick in den aktuellen Status des Baus. Auf dieser Grundlage werden Fehler minimiert, da wir mit einem Echtzeitüberblick am Besprechungstisch sitzen und wichtige Entscheidungen sofort treffen können.",
    },
    cta: {
      heading: "Sollen wir bei Ihrem nächsten Bauprojekt unterstützen?",
      body: "Ob Sie vor einem kleineren Bauprojekt oder einem größeren, komplexen Bau stehen – wir sind bereit, Ihnen sicher ans Ziel zu helfen. Nehmen Sie Kontakt auf für ein unverbindliches Gespräch darüber, wie MI Support Ihr Projekt voranbringen kann.",
      call: "Anrufen +45 5555 1212",
      write: "Schreiben Sie uns",
    },
    blog: {
      heading: "Lesen Sie unsere neuesten Blogbeiträge",
      intro:
        "Lernen Sie MI Support durch unsere Artikel hier auf der Seite kennen. Wir teilen laufend alles von Unternehmensnachrichten über Tipps und Tricks bis hin zu interessanten Perspektiven auf die Baubranche.",
      readMore: "Mehr erfahren",
    },
  },
};
